import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Bell, Mail, Clock, CheckSquare, Users, Heart, Store, Save, MessageSquare, Send, ExternalLink, Filter } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  getWeddings, getNotifications, getNotificationPreferences,
  updateNotificationPreferences, markNotificationRead, markAllNotificationsRead,
  deleteNotification, getMyInquiries, markInquiryCoupleRead,
  getVendorInquiries, markVendorInquiryRead, markVendorInquiriesReadAll,
  getAdminInquiries
} from '../services/api';

function NotificationSettings() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Shared state
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Couple/helper state
  const [weddingId, setWeddingId] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [saving, setSaving] = useState(false);

  // Couple inquiry replies state
  const [coupleInquiries, setCoupleInquiries] = useState([]);

  // Vendor state
  const [vendorInquiries, setVendorInquiries] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('all');

  // Admin state
  const [adminInquiries, setAdminInquiries] = useState([]);
  const [adminFilter, setAdminFilter] = useState('all');

  // Tabs - role-dependent
  const [activeTab, setActiveTab] = useState('notifications');

  const isCouple = user?.role === 'couple';
  const isHelper = user?.role === 'helper';
  const isVendor = user?.role === 'vendor';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    if (isCouple || isHelper) {
      loadCoupleData();
    } else if (isVendor) {
      setActiveTab('inquiries');
      loadVendorData();
    } else if (isAdmin) {
      setActiveTab('inquiries');
      loadAdminData();
    } else {
      // e.g. vendor_employee: the navbar bell's "View all" lands here, and
      // nothing ever cleared the spinner
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, authLoading]);

  // --- Couple/Helper Data ---
  const loadCoupleData = async () => {
    try {
      const [weddingsRes, notifsRes] = await Promise.all([
        getWeddings(),
        getNotifications({ limit: 50 }),
      ]);
      setNotifications(notifsRes.data.notifications || []);
      setUnreadCount(notifsRes.data.unreadCount || 0);

      if (weddingsRes.data.length > 0) {
        const w = weddingsRes.data[0];
        setWeddingId(w.id);
        setWedding(w);
        const prefsRes = await getNotificationPreferences(w.id);
        setPrefs(prefsRes.data);
      }

      if (isCouple) {
        const inqRes = await getMyInquiries();
        setCoupleInquiries(inqRes.data || []);
      }
    } catch {
      // Otherwise the page claims "no notifications" / "create a wedding first"
      setMessage({ type: 'error', text: t('notifications.loadFailed') });
    }
    setLoading(false);
  };

  // --- Vendor Data ---
  const loadVendorData = async () => {
    try {
      const res = await getVendorInquiries();
      setVendorInquiries(res.data || []);
    } catch (err) {
      console.error('Failed to load vendor inquiries:', err);
      setVendorInquiries([]);
    }
    setLoading(false);
  };

  // --- Admin Data ---
  const loadAdminData = async () => {
    try {
      const res = await getAdminInquiries();
      setAdminInquiries(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  // --- Couple notification handlers ---
  const handleSavePrefs = async () => {
    if (!weddingId || !prefs) return;
    setSaving(true);
    try {
      const res = await updateNotificationPreferences(weddingId, prefs);
      setPrefs(res.data);
      setMessage({ type: 'success', text: t('notifications.prefsSaved') });
    } catch {
      setMessage({ type: 'error', text: t('notifications.prefsSaveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(notifications.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications(notifications.filter((n) => n.id !== id));
    } catch {}
  };

  const handleCoupleInquiryRead = async (id) => {
    try {
      await markInquiryCoupleRead(id);
      setCoupleInquiries(prev => prev.map(inq => inq.id === id ? { ...inq, couple_read: true } : inq));
    } catch {}
  };

  // --- Vendor handlers ---
  const handleVendorMarkRead = async (id) => {
    try {
      await markVendorInquiryRead(id);
      setVendorInquiries(prev => prev.map(inq => inq.id === id ? { ...inq, vendor_read: true } : inq));
    } catch {}
  };

  const handleVendorMarkAllRead = async () => {
    try {
      await markVendorInquiriesReadAll();
      setVendorInquiries(prev => prev.map(inq => ({ ...inq, vendor_read: true })));
    } catch {}
  };

  // --- Shared helpers ---
  const notifIcon = (type) => {
    switch (type) {
      case 'checklist_assigned': return <CheckSquare size={16} className="text-blue" />;
      case 'checklist_completed': return <CheckSquare size={16} className="text-green" />;
      case 'checklist_updated': return <CheckSquare size={16} className="text-orange" />;
      case 'invite_helper': case 'helper_joined': return <Users size={16} className="text-purple" />;
      case 'invite_delegate': case 'delegate_joined': return <Users size={16} className="text-pink" />;
      case 'task_reminder': return <Clock size={16} className="text-orange" />;
      case 'wedding_reminder': return <Heart size={16} className="text-red" />;
      case 'vendor_sync_complete': return <Store size={16} className="text-blue" />;
      default: return <Bell size={16} />;
    }
  };

  const formatTime = (t) => {
    if (!t) return '';
    const d = new Date(t);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const statusLabels = { new: t('notifications.statusNew'), read: t('notifications.statusRead'), replied: t('notifications.statusReplied'), booked: t('notifications.statusBooked'), unbooked: t('notifications.statusNotBooked'), declined: t('notifications.statusDeclined') };

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  // ============================================================
  // VENDOR VIEW
  // ============================================================
  if (isVendor) {
    const vendorUnread = vendorInquiries.filter(i => !i.vendor_read).length;
    const filteredVendor = vendorFilter === 'all' ? vendorInquiries
      : vendorFilter === 'unread' ? vendorInquiries.filter(i => !i.vendor_read)
      : vendorInquiries.filter(i => i.status === vendorFilter);

    return (
      <div className="notifications-page">
        <div className="container">
          <div className="page-header">
            <div>
              <h1><Bell size={28} /> {t('notifications.title')}</h1>
              <p>{vendorUnread > 0 ? `${vendorUnread} ${t('notifications.unreadInquiries')}` : t('notifications.allCaughtUp')}</p>
            </div>
          </div>

          {/* Filter + Mark All */}
          <div className="notif-page-toolbar">
            <div className="notif-page-filters">
              {[
                { key: 'all', label: `${t('notifications.allTab')} (${vendorInquiries.length})` },
                { key: 'unread', label: `${t('notifications.unreadTab')} (${vendorUnread})` },
                { key: 'new', label: t('notifications.newTab') },
                { key: 'replied', label: t('notifications.repliedTab') },
                { key: 'booked', label: t('notifications.bookedTab') },
              ].map(f => (
                <button key={f.key} onClick={() => setVendorFilter(f.key)}
                  className={`notif-filter-btn ${vendorFilter === f.key ? 'active' : ''}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {vendorUnread > 0 && (
              <button className="btn btn-outline btn-sm" onClick={handleVendorMarkAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {/* Inquiry List */}
          {filteredVendor.length === 0 ? (
            <div className="notif-empty-full">
              <MessageSquare size={48} style={{ color: '#d1d5db' }} />
              <h3>{vendorInquiries.length === 0 ? t('notifications.noInquiriesYet') : t('notifications.noMatching')}</h3>
              <p>{t('notifications.inquiriesAppear')}</p>
            </div>
          ) : (
            <div className="notif-page-list">
              {filteredVendor.map(inq => (
                <div key={inq.id}
                  className={`notif-page-item ${inq.vendor_read ? 'read' : 'unread'}`}
                  onClick={() => {
                    if (!inq.vendor_read) handleVendorMarkRead(inq.id);
                    navigate(`/vendor-portal?tab=inquiries&inq=${inq.id}`);
                  }}
                >
                  <div className="notif-page-icon">
                    <MessageSquare size={18} />
                  </div>
                  <div className="notif-page-content">
                    <div className="notif-page-title">
                      {inq.first_name} {inq.last_name}
                      <span className={`notif-status-badge status-${inq.status}`}>
                        {statusLabels[inq.status] || inq.status}
                      </span>
                    </div>
                    <p className="notif-page-msg">{inq.message ? inq.message.substring(0, 120) + (inq.message.length > 120 ? '...' : '') : 'No message'}</p>
                    {inq.vendor_reply && (
                      <p className="notif-page-reply"><Send size={12} /> {t('notifications.youReplied')} "{inq.vendor_reply.substring(0, 80)}{inq.vendor_reply.length > 80 ? '...' : ''}"</p>
                    )}
                    <span className="notif-page-time">{formatTime(inq.created_at)}</span>
                  </div>
                  {!inq.vendor_read && <span className="notif-page-dot"></span>}
                </div>
              ))}
            </div>
          )}

          <div className="notif-page-footer-link">
            <Link to="/vendor-portal?tab=inquiries"><Store size={14} /> {t('notifications.viewDetailedPage')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // ADMIN VIEW
  // ============================================================
  if (isAdmin) {
    const adminNew = adminInquiries.filter(i => i.status === 'new').length;
    const adminUnread = adminInquiries.filter(i => !i.admin_read).length;
    const filteredAdmin = adminFilter === 'all' ? adminInquiries
      : adminInquiries.filter(i => i.status === adminFilter);

    return (
      <div className="notifications-page">
        <div className="container">
          <div className="page-header">
            <div>
              <h1><Bell size={28} /> {t('notifications.title')}</h1>
              <p>{adminUnread > 0 ? `${adminUnread} ${t('notifications.unreadInquiries')}` : t('notifications.allCaughtUp')}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="notif-page-toolbar">
            <div className="notif-page-filters">
              {[
                { key: 'all', label: `${t('notifications.allTab')} (${adminInquiries.length})` },
                { key: 'new', label: `${t('notifications.newTab')} (${adminNew})` },
                { key: 'replied', label: t('notifications.repliedTab') },
                { key: 'booked', label: t('notifications.bookedTab') },
                { key: 'declined', label: t('notifications.statusDeclined') },
              ].map(f => (
                <button key={f.key} onClick={() => setAdminFilter(f.key)}
                  className={`notif-filter-btn ${adminFilter === f.key ? 'active' : ''}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Inquiry List */}
          {filteredAdmin.length === 0 ? (
            <div className="notif-empty-full">
              <MessageSquare size={48} style={{ color: '#d1d5db' }} />
              <h3>{adminInquiries.length === 0 ? t('notifications.noInquiriesYet') : t('notifications.noMatching')}</h3>
              <p>{t('notifications.inquiriesAppear')}</p>
            </div>
          ) : (
            <div className="notif-page-list">
              {filteredAdmin.map(inq => (
                <div key={inq.id}
                  className={`notif-page-item ${!inq.admin_read ? 'unread' : 'read'}`}
                  onClick={() => navigate(`/admin?tab=inquiries&inq=${inq.id}`)}
                >
                  <div className="notif-page-icon">
                    <MessageSquare size={18} />
                  </div>
                  <div className="notif-page-content">
                    <div className="notif-page-title">
                      {inq.first_name} {inq.last_name} → {inq.vendor_name}
                      <span className={`notif-status-badge status-${inq.status}`}>
                        {statusLabels[inq.status] || inq.status}
                      </span>
                    </div>
                    <p className="notif-page-msg">{inq.message ? inq.message.substring(0, 120) + (inq.message.length > 120 ? '...' : '') : 'No message'}</p>
                    {inq.vendor_reply && (
                      <p className="notif-page-reply"><Send size={12} /> {t('notifications.vendorReplied')} "{inq.vendor_reply.substring(0, 80)}{inq.vendor_reply.length > 80 ? '...' : ''}"</p>
                    )}
                    <span className="notif-page-time">{formatTime(inq.created_at)}</span>
                  </div>
                  {inq.status === 'new' && <span className="notif-page-dot"></span>}
                </div>
              ))}
            </div>
          )}

          <div className="notif-page-footer-link">
            <Link to="/admin?tab=inquiries"><Filter size={14} /> {t('notifications.viewDetailedPage')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // OTHER ROLES (vendor_employee): no inbox or settings on this page
  // ============================================================
  if (!isCouple && !isHelper) {
    return (
      <div className="notifications-page">
        <div className="container">
          <div className="page-header">
            <div>
              <h1><Bell size={28} /> {t('notifications.title')}</h1>
            </div>
          </div>
          <div className="notif-empty-full">
            <Bell size={48} style={{ color: '#d1d5db' }} />
            <p>{t('notifications.noSettingsForRole')}</p>
            <Link to="/my-projects" className="btn btn-primary" style={{ marginTop: '12px' }}>{t('nav.myProjects')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // COUPLE / HELPER VIEW
  // ============================================================
  const coupleReplied = coupleInquiries.filter(i => i.vendor_reply && !i.couple_read).length;

  return (
    <div className="notifications-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1><Bell size={28} /> {t('notifications.title')}</h1>
            <p>{unreadCount + coupleReplied > 0
              ? `${unreadCount + coupleReplied} ${t('notifications.unreadInquiries')}`
              : t('notifications.allCaughtUp')}</p>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
            <button className="alert-close" onClick={() => setMessage(null)}>&times;</button>
          </div>
        )}

        {/* Tabs */}
        <div className="category-tabs">
          <button className={activeTab === 'notifications' ? 'active' : ''} onClick={() => setActiveTab('notifications')}>
            <Bell size={16} /> {t('notifications.notificationsTab')} {unreadCount > 0 && <span className="tab-count">{unreadCount}</span>}
          </button>
          {isCouple && (
            <button className={activeTab === 'inquiries' ? 'active' : ''} onClick={() => setActiveTab('inquiries')}>
              <MessageSquare size={16} /> {t('notifications.inquiryRepliesTab')} {coupleReplied > 0 && <span className="tab-count">{coupleReplied}</span>}
            </button>
          )}
          <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
            <Mail size={16} /> {t('notifications.emailSettingsTab')}
          </button>
        </div>

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="notifications-section">
            {notifications.length > 0 && unreadCount > 0 && (
              <div className="notif-actions">
                <button className="btn btn-outline btn-sm" onClick={handleMarkAllRead}>
                  {t('notifications.markAllRead')}
                </button>
              </div>
            )}

            {notifications.length === 0 ? (
              <div className="empty-state-sm">
                <Bell size={36} />
                <p>{t('notifications.noNotificationsYet')}</p>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.map((n) => (
                  <div key={n.id} className={`notification-item ${n.is_read ? 'read' : 'unread'}`}>
                    <div className="notif-icon">{notifIcon(n.type)}</div>
                    <div className="notif-content" onClick={() => !n.is_read && handleMarkRead(n.id)}>
                      <h4>{n.title}</h4>
                      <p>{n.message}</p>
                      <span className="notif-time">
                        {new Date(n.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                        {n.is_email_sent && <span className="email-badge"><Mail size={10} /> {t('notifications.emailSent')}</span>}
                      </span>
                    </div>
                    <button className="notif-delete" onClick={() => handleDelete(n.id)} title="Delete">
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inquiry Replies Tab (Couple only) */}
        {activeTab === 'inquiries' && isCouple && (
          <div className="notifications-section">
            {coupleInquiries.length === 0 ? (
              <div className="empty-state-sm">
                <MessageSquare size={36} />
                <p>{t('notifications.noInquiriesBrowse')}</p>
                <Link to="/vendors" className="btn btn-primary" style={{ marginTop: '12px' }}>{t('notifications.browseVendors')}</Link>
              </div>
            ) : (
              <div className="notif-page-list">
                {coupleInquiries.map(inq => (
                  <div key={inq.id}
                    className={`notif-page-item ${inq.vendor_reply && !inq.couple_read ? 'unread' : 'read'}`}
                    onClick={() => {
                      if (inq.vendor_reply && !inq.couple_read) handleCoupleInquiryRead(inq.id);
                      navigate('/my-inquiries');
                    }}
                  >
                    <div className="notif-page-icon">
                      {inq.vendor_reply ? <Send size={18} /> : <MessageSquare size={18} />}
                    </div>
                    <div className="notif-page-content">
                      <div className="notif-page-title">
                        {inq.business_name}
                        <span className={`notif-status-badge status-${inq.status}`}>
                          {statusLabels[inq.status] || inq.status}
                        </span>
                      </div>
                      <p className="notif-page-msg">{inq.message ? inq.message.substring(0, 100) + (inq.message.length > 100 ? '...' : '') : ''}</p>
                      {inq.vendor_reply && (
                        <p className="notif-page-reply"><Send size={12} /> Reply: "{inq.vendor_reply.substring(0, 80)}{inq.vendor_reply.length > 80 ? '...' : ''}"</p>
                      )}
                      <span className="notif-page-time">{formatTime(inq.updated_at || inq.created_at)}</span>
                    </div>
                    {inq.vendor_reply && !inq.couple_read && <span className="notif-page-dot"></span>}
                  </div>
                ))}
              </div>
            )}

            <div className="notif-page-footer-link">
              <Link to="/my-inquiries"><ExternalLink size={14} /> {t('notifications.viewDetailedPage')}</Link>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && prefs && (
          <div className="notif-settings-section">
            <div className="settings-card">
              <h3><Mail size={18} /> {t('notifications.emailPreferences')}</h3>
              <p className="section-desc">
                {t('notifications.chooseNotifications')}
              </p>

              <div className="settings-grid">
                <div className="setting-group">
                  <h4><CheckSquare size={16} /> {t('notifications.checklistGroup')}</h4>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_checklist_assigned}
                      onChange={(e) => setPrefs({ ...prefs, email_checklist_assigned: e.target.checked })} />
                    <span>{t('notifications.taskAssigned')}</span>
                  </label>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_checklist_completed}
                      onChange={(e) => setPrefs({ ...prefs, email_checklist_completed: e.target.checked })} />
                    <span>{t('notifications.taskCompleted')}</span>
                  </label>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_checklist_updated}
                      onChange={(e) => setPrefs({ ...prefs, email_checklist_updated: e.target.checked })} />
                    <span>{t('notifications.taskUpdated')}</span>
                  </label>
                </div>

                <div className="setting-group">
                  <h4><Clock size={16} /> {t('notifications.remindersGroup')}</h4>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_task_reminders}
                      onChange={(e) => setPrefs({ ...prefs, email_task_reminders: e.target.checked })} />
                    <span>{t('notifications.taskDueReminders')}</span>
                  </label>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_wedding_reminders}
                      onChange={(e) => setPrefs({ ...prefs, email_wedding_reminders: e.target.checked })} />
                    <span>{t('notifications.weddingCountdown')}</span>
                  </label>
                  <div className="setting-input">
                    <label>{t('notifications.remindDaysBefore')}</label>
                    <input type="number" min="1" max="30" value={prefs.reminder_days_before}
                      onChange={(e) => setPrefs({ ...prefs, reminder_days_before: parseInt(e.target.value) || 3 })} />
                  </div>
                </div>

                <div className="setting-group">
                  <h4><Users size={16} /> {t('notifications.teamGroup')}</h4>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_helper_joined}
                      onChange={(e) => setPrefs({ ...prefs, email_helper_joined: e.target.checked })} />
                    <span>{t('notifications.helperAccepts')}</span>
                  </label>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_delegate_joined}
                      onChange={(e) => setPrefs({ ...prefs, email_delegate_joined: e.target.checked })} />
                    <span>{t('notifications.delegateAccepts')}</span>
                  </label>
                </div>

                <div className="setting-group">
                  <h4><Store size={16} /> {t('notifications.vendorsGroup')}</h4>
                  <label className="setting-toggle">
                    <input type="checkbox" checked={prefs.email_vendor_updates}
                      onChange={(e) => setPrefs({ ...prefs, email_vendor_updates: e.target.checked })} />
                    <span>{t('notifications.vendorUpdates')}</span>
                  </label>
                </div>
              </div>

              <div className="settings-actions">
                <button className="btn btn-primary" onClick={handleSavePrefs} disabled={saving}>
                  <Save size={16} /> {saving ? 'Saving...' : t('notifications.savePreferences')}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && !prefs && (
          <div className="empty-state-sm">
            <Mail size={36} />
            <p>{t('notifications.createWeddingFirst')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationSettings;
